/**
 * Platform picker. Only platforms with a connected account can be drafted for —
 * everything else is disabled with the reason, so nobody writes a post that can
 * never publish.
 */

import { Link } from 'react-router-dom';
import { Check, Plug } from 'lucide-react';
import { PLATFORM_LIST } from '../../../shared/platforms';
import type { PlatformConnectionStatus } from '../../../shared/api';
import type { PlatformId, SocialAccount } from '../../../shared/types';
import { Button, PlatformChip, Tooltip } from '../ui';
import { cn } from '../../lib/utils';

export interface PlatformSelectorProps {
  accounts: SocialAccount[];
  statuses: PlatformConnectionStatus[];
  selected: PlatformId[];
  onToggle: (platform: PlatformId) => void;
  disabled?: boolean;
  className?: string;
}

export function PlatformSelector({
  accounts,
  statuses,
  selected,
  onToggle,
  disabled,
  className,
}: PlatformSelectorProps) {
  const accountFor = new Map<PlatformId, SocialAccount>();
  for (const account of accounts) {
    if (!accountFor.has(account.platform)) accountFor.set(account.platform, account);
  }
  const statusFor = new Map(statuses.map((status) => [status.platform, status]));

  const connectable = PLATFORM_LIST.filter(
    (spec) => !spec.comingSoon && !accountFor.has(spec.id),
  ).length;
  const connected = PLATFORM_LIST.filter((spec) => accountFor.has(spec.id));
  const allSelected =
    connected.length > 0 && connected.every((spec) => selected.includes(spec.id));

  return (
    <div className={cn('space-y-2.5', className)}>
      <div className="flex flex-wrap gap-2">
        {PLATFORM_LIST.map((spec) => {
          const account = accountFor.get(spec.id);
          const status = statusFor.get(spec.id);
          const isSelected = selected.includes(spec.id);
          const blocked = spec.comingSoon || !account;

          const hint = spec.comingSoon
            ? `${spec.name} publishing is coming soon.`
            : !account
              ? isSelected
                ? `No ${spec.shortName} account is connected any more — click to drop this variant.`
                : `Connect ${spec.shortName} to draft for it.`
              : account.status === 'simulated'
                ? `${account.displayName} · simulated account`
                : account.displayName;

          return (
            <Tooltip key={spec.id} content={hint}>
              <PlatformChip
                platform={spec.id}
                selected={isSelected}
                // A selected platform stays clickable even when its account went
                // away, so the variant can still be removed.
                disabled={(blocked && !isSelected) || disabled}
                onClick={() => onToggle(spec.id)}
                trailing={
                  isSelected ? (
                    <Check className="size-3.5 text-accent" aria-hidden />
                  ) : account?.status === 'simulated' ? (
                    <span className="size-1.5 rounded-full bg-warning" aria-hidden />
                  ) : !blocked && status && status.connectedCount > 1 ? (
                    <span className="pap-tnum text-[10px] text-subtle">
                      {status.connectedCount}
                    </span>
                  ) : null
                }
              />
            </Tooltip>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-subtle">
        {connected.length > 1 && (
          <Button
            variant="link"
            size="xs"
            disabled={disabled}
            onClick={() => {
              for (const spec of connected) {
                const isSelected = selected.includes(spec.id);
                if (allSelected ? isSelected : !isSelected) onToggle(spec.id);
              }
            }}
          >
            {allSelected ? 'Clear all' : 'Select every connected account'}
          </Button>
        )}
        {connectable > 0 && (
          <Link
            to="/connections"
            className="inline-flex items-center gap-1.5 text-accent underline-offset-4 hover:underline"
          >
            <Plug className="size-3.5" aria-hidden />
            Connect {connectable} more platform{connectable === 1 ? '' : 's'}
          </Link>
        )}
      </div>
    </div>
  );
}
